// components/ChatInterface.js - Updated with Enterprise Technologist styling
import React, { useState, useRef, useEffect } from 'react';
import './ChatInterface.css';
import { ArrowLeft, Send, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const ChatInterface = ({ agent, messages, onSendMessage, isLoading, onBack }) => {
  const [input, setInput] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [input]);

  // Reset copy icon after 2 seconds
  useEffect(() => {
    if (copiedIndex !== null) {
      const timer = setTimeout(() => {
        setCopiedIndex(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedIndex]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    onSendMessage(input);
    setInput('');
  };

  // Handle Enter key to send (Shift+Enter for new line)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Copy message content to clipboard
  const handleCopy = (index) => {
    const message = messages[index];
    navigator.clipboard.writeText(message.content);
    setCopiedIndex(index);
  };

  // Custom renderer for code blocks with syntax highlighting
  const CodeBlock = ({ node, inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    return !inline && match ? (
      <div className="code-block-container">
        <div className="code-block-header">
          <span className="code-language-label">{match[1]}</span>
          <button 
            className="copy-code-button" 
            onClick={() => {
              navigator.clipboard.writeText(String(children).replace(/\n$/, ''));
            }}
            aria-label={`Copy ${match[1]} code`}
          >
            <Copy size={14} />
          </button>
        </div>
        <SyntaxHighlighter
          style={atomDark}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      </div>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  };

  // Render citations if they exist
  const renderCitations = (citations) => {
    if (!citations || citations.length === 0) return null;
    
    return (
      <div className="citations">
        <h4>Citations</h4>
        <ol>
          {citations.map((citation, index) => (
            <li key={index}>
              {citation.text} {citation.page && `(Page ${citation.page})`}
            </li>
          ))}
        </ol>
      </div>
    );
  };

  // Get book ID for badge class
  const getBadgeClass = (bookId) => {
    if (!bookId) return "badge-product";
    
    const category = bookId.split('-')[0];
    switch (category) {
      case 'retail': return 'badge-retail';
      case 'media': return 'badge-media';
      case 'manufacturing': return 'badge-manufacturing';
      case 'product': return 'badge-product';
      case 'payments': return 'badge-payments';
      case 'contact': return 'badge-contactcenter';
      default: return 'badge-product';
    }
  };

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        <div className="agent-info">
          <img src={agent.avatar} alt={agent.name} className="agent-avatar" />
          <div>
            <h2>
              {agent.name.split(' ')[0]} <span>{agent.name.split(' ')[1]}</span>
            </h2>
            <p>{agent.bookTitle}</p>
          </div>
        </div>
      </div>

      <div className="messages-container">
        {messages.map((message, index) => (
          <div key={index} className={`message ${message.role}`}>
            {/* Add avatar based on message role */}
            <div className="message-avatar">
              <img 
                src={message.role === 'assistant' ? agent.avatar : '/avatars/user.png'} 
                alt={message.role === 'assistant' ? agent.name : 'User'} 
              />
            </div>
            
            <div className="message-content">
              <ReactMarkdown 
                components={{
                  code: CodeBlock
                }}
              >
                {message.content}
              </ReactMarkdown>
              
              {message.citations && renderCitations(message.citations)}
            </div>
            
            <button 
              className="copy-button" 
              onClick={() => handleCopy(index)}
              aria-label="Copy message"
            >
              {copiedIndex === index ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
        ))}
        
        {isLoading && (
          <div className="message assistant">
            <div className="message-avatar">
              <img src={agent.avatar} alt={agent.name} />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="input-container" onSubmit={handleSubmit}>
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Message Enterprise Technologist..."
            rows={1}
          />
          <button 
            type="submit" 
            className="send-button"
            disabled={!input.trim() || isLoading}
            aria-label="Send message"
          >
            <Send size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;